import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, ThemeProvider, Typography, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import axiosService from "../utils/axios";
import GenerationRecipe from "../components/GenerationRecipe";
import Loading from "../components/Loading";
import { addRecipe } from "../store/slices/recipes";
import { cleanJsonResponse } from "../utils/helpers/generalHelper";
import { Menu } from "@mui/icons-material";
import RecipeDrawer from "../components/RecipeDrawer";
import type { IParameter, IAdditionalParameter } from "../types/parameter";
import { tips } from "../utils/tips";

export type Parameters = IParameter[];

interface IResponseItem {
    name: string;
}

type ApiResponse = Record<string, IResponseItem[] | string[]>;

type Parameter = {
    label: string;
    options: string[];
    selected: string[];
};

const randomTip = tips[Math.floor(Math.random() * tips.length)];

const GenerateParameters: React.FC = () => {
    const [selectedParameters, setSelectedParameters] = useState<Parameters | []>([]);
    const [additionalParameters, setAdditionalParameters] = useState<IAdditionalParameter>({
        strict: true,
        additional: true,
        level: null,
        cookTime: 0,
        count: 1,
    });
    const [loading, setLoading] = useState(true);
    const [recipeLoading, setRecipeLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const recipesState = useSelector((state: RootState) => state.recipes);
    const recentRecipes = useMemo(() => recipesState.recentParameter ?? [], [recipesState.recentParameter]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;

    const dispatch = useDispatch();

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const capitalize = useCallback((word: string) => word.charAt(0).toUpperCase() + word.slice(1), []);

    useEffect(() => {
        const fetchParams = async () => {
            try {
                setLoading(true);

                const { data } = await axiosService.get<ApiResponse>(`${process.env.REACT_APP_API_URL}/recipes/fetchParameters`);

                const parameters: Parameter[] = Object.entries(data).map(([key, values]) => {
                    const label = capitalize(key);

                    const names =
                        Array.isArray(values) && values.length > 0 && typeof values[0] === "object"
                            ? (values as IResponseItem[]).map((item) => item.name)
                            : (values as string[]);

                    const options = Array.from(
                        new Set(
                            key !== "allergies"
                                ? names
                                : [
                                      ...names,
                                      ...(Array.isArray(data.ingredients)
                                          ? data.ingredients.map((item) => {
                                                if (typeof item === "string") return item;
                                                else return item.name;
                                            })
                                          : []),
                                  ]
                        )
                    );

                    return { label, options, selected: [] };
                });
                setSelectedParameters(parameters);
                setError("");
            } catch (err) {
                setError("Failed to load parameters");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchParams();
    }, [capitalize]);

    const handleParameterChange = useCallback((label: string, newSelected: string[]) => {
        setSelectedParameters((prev) => prev.map((param) => (param.label === label ? { ...param, selected: newSelected } : param)));
    }, []);

    const handleTagClick = useCallback(
        (label: string, itemToRemove: string) => {
            const target = selectedParameters.find((p) => p.label === label);
            if (!target) throw new Error("Category not found.");
            handleParameterChange(
                label,
                target.selected.filter((item) => item !== itemToRemove)
            );
        },
        [selectedParameters, handleParameterChange]
    );

    const handleGenerate = useCallback(() => {
        setRecipeLoading(true);
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/recipes/generate/parameters`, {
                selectedParameters,
                additionalParameters,
                username: isGuest ? null : authState.account?.username,
                localRecentRecipes: recentRecipes.map((recipe) => recipe.name),
            })
            .then((res) => {
                const cleanedRecipe = cleanJsonResponse(res.data.body.data);
                const recipeJSON = JSON.parse(cleanedRecipe);
                dispatch(
                    addRecipe({
                        name: recipeJSON.name,
                        description: recipeJSON.description,
                        ingredients: recipeJSON.ingredients,
                        tags: recipeJSON.tags,
                        procedure: recipeJSON.procedure,
                        generationId: res.data.body.generationId,
                    })
                );

                setError("");
            })
            .catch((error) => {
                setError(error.response?.data?.body || "Failed to generate recipe");
            })
            .finally(() => setRecipeLoading(false));
    }, [selectedParameters, additionalParameters, isGuest, authState, recentRecipes, dispatch]);

    const updateAdditionalParameters = useCallback(<K extends keyof IAdditionalParameter>(key: K, value: IAdditionalParameter[K]) => {
        setAdditionalParameters((prev) => {
            let count = prev.count;
            if (key === "level") {
                count += prev[key] === null ? 1 : value === null ? -1 : 0;
            } else {
                count += prev[key] !== value ? (value ? 1 : -1) : 0;
            }
            return { ...prev, [key]: value, count };
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ height: "calc(100dvh - 65px)", display: "flex", flexDirection: "column", position: "relative", width: "100%" }}>
                <Box sx={{ position: "absolute", top: 0, left: 0, display: "flex", zIndex: 1, alignItems: "center", m: 1 }}>
                    <IconButton disabled={recipeLoading || loading} onClick={() => setDrawerOpen(true)}>
                        <Menu />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, position: "relative", justifyItems: "center", alignContent: "center", width: "100%" }}>
                    <RecipeDrawer
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        selectedParameters={selectedParameters}
                        additionalParameters={additionalParameters}
                        updateAdditionalParameters={updateAdditionalParameters}
                        parameterChange={handleParameterChange}
                        tagClick={handleTagClick}
                        onSubmit={handleGenerate}
                    />

                    <Box
                        sx={{
                            height: "calc(100dvh - 100px)",
                            maxWidth: 1200,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            px: 2,
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {recipeLoading || loading ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                <Loading />
                                <Typography variant="h6" sx={{ fontFamily: "Playfair Display", fontWeight: 600, mt: 3 }}>
                                    {randomTip}
                                </Typography>
                            </Box>
                        ) : error ? (
                            <Typography sx={{ mt: "15%" }}>{error}</Typography>
                        ) : !recentRecipes || recentRecipes.length === 0 ? (
                            <Typography variant="h6" sx={{ mt: "15%", textAlign: "center" }}>
                                Press the menu (☰) on the top left to start generating.
                            </Typography>
                        ) : (
                            <GenerationRecipe recentRecipes={recentRecipes} type="parameters" />
                        )}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default GenerateParameters;
